import { useMutation } from "@apollo/client"
import React from "react"
import { useTranslation } from "react-i18next"
import { useRecoilState } from "recoil"
import { DELETE_PROJECT } from "../../api/QuadriApi"
import { addSnackSelector } from "../StateSelectors"
import { ConfigStatus, startBackupTimeoutReload } from "./ConfigMain"
import { track } from "../../util/gainsight"
import DeleteModal from "./DeleteModal"
import Modal from "../Modal"
import { AccountAdmin } from "../../types"

interface DeleteProjectParams {
  connectProjectId: string
  projectStatus: ConfigStatus | undefined
  isAllowedToEnableAndRemoveExtensionbyPolicy: boolean
  extensionOwnershipName: string | undefined
  accountAdmins: AccountAdmin[]
  isTcpsLicensed: boolean
}

interface DeleteProjectReturn {
  buttonDeleteProject: (() => void) | undefined
  deleteModals: React.ReactNode
  isDeleting: boolean
}

export const DeleteProject = (params: DeleteProjectParams): DeleteProjectReturn => {
  const {
    connectProjectId,
    projectStatus,
    isAllowedToEnableAndRemoveExtensionbyPolicy,
    extensionOwnershipName,
    accountAdmins,
    isTcpsLicensed
  } = params

  const { t } = useTranslation("config")
  const [, addSnack] = useRecoilState(addSnackSelector)
  const [deleting, setDeleting] = React.useState(false)
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const [showMessage, setShowMessage] = React.useState<boolean>(false)
  const [showAllAdmins, setShowAllAdmins] = React.useState(false)

  const showModal = () => setShowDeleteModal(true)
  const closeModal = () => setShowDeleteModal(false)
  const showMessageModal = () => setShowMessage(true)
  const closeMessageModal = () => setShowMessage(false)

  const displayedAdmins = showAllAdmins ? accountAdmins : accountAdmins.slice(0, 10)

  // Mutation used for disabling after the project has been created
  const [deleteProjectMutation] = useMutation(DELETE_PROJECT, {
    onCompleted: (data: any) => startBackupTimeoutReload(t, addSnack)
  })

  const deleteProject = (purge: boolean) => {
    track("project_delete", { connectProjectId: connectProjectId })
    setDeleting(true)
    deleteProjectMutation({
      variables: {
        deleteProjectRequest: {
          connectProjectId,
          purge,
          isTcpsLicensed
        }
      }
    })
  }

  const isDeleting = deleting || projectStatus === "Deleting"

  const buttonDeleteProject = !isDeleting
    ? isAllowedToEnableAndRemoveExtensionbyPolicy
      ? showModal
      : showMessageModal
    : undefined

  const deleteModals = (
    <>
      {showMessage && (
        <Modal heading={t("extensionPolicy.heading")} closeModal={closeMessageModal} large={true}>
          <div>{t("extensionPolicy.remove") + extensionOwnershipName + t("extensionPolicy.commonText")}</div>
          <br />
          <ul>
            {displayedAdmins.map((admin: AccountAdmin, index: number) => (
              <li key={index}>
                <strong>{admin.fullName}</strong> ({admin.email})
              </li>
            ))}
          </ul>
          {/* Show "Show More" or "Show Less" link based on admin count */}
          {accountAdmins.length > 10 && !showAllAdmins && (
            <button
              className="m-0 p-0"
              onClick={() => setShowAllAdmins(true)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#005F9E" }}
            >
              {t("extensionPolicy.showMore")}
            </button>
          )}
          {showAllAdmins && (
            <button
              onClick={() => setShowAllAdmins(false)}
              className="m-0 p-0"
              style={{ background: "none", border: "none", cursor: "pointer", color: "#005F9E" }}
            >
              {t("extensionPolicy.showLess")}
            </button>
          )}
        </Modal>
      )}
      {showDeleteModal && <DeleteModal closeModal={closeModal} deleteProject={deleteProject} />}
    </>
  )

  return {
    buttonDeleteProject,
    deleteModals,
    isDeleting
  }
}
